import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Member/SubscriptionMobilePricing/ctaMatrix";
import type { SupportedMobilePricingPlanType } from "./selection";
import type { MemberPlanInfo } from "@/src/hooks";

const I18n = I18nUtil.createScoped(Translations);

type CtaMode = "hidden" | "current" | "action";

interface ResolveCtaInput {
  locale?: string;
  userPlanInfo?: MemberPlanInfo;
  selectedPlanType: SupportedMobilePricingPlanType;
  selectedOptionHasTrialBadge: boolean;
  hasTrialAvailable: boolean;
  isIOSApp: boolean;
}

interface ResolveCtaResult {
  mode: CtaMode;
  text: string;
  showCrypto: boolean;
}

const resolveCurrentPlanType = (
  userPlanInfo?: MemberPlanInfo,
): SupportedMobilePricingPlanType => {
  if (userPlanInfo?.payCycle === "LIFETIME") {
    return "lifetime";
  }
  if (userPlanInfo?.payCycle === "WEEK") {
    return "weekly";
  }
  if (userPlanInfo?.payCycle === "MONTH") {
    return "monthly";
  }
  return "yearly";
};

const isSelectedCurrentPlan = ({
  userPlanInfo,
  selectedPlanType,
}: {
  userPlanInfo?: MemberPlanInfo;
  selectedPlanType: SupportedMobilePricingPlanType;
}): boolean => {
  if (!userPlanInfo?.isMember) {
    return false;
  }
  return resolveCurrentPlanType(userPlanInfo) === selectedPlanType;
};

export const resolveMobilePricingCta = ({
  locale,
  userPlanInfo,
  selectedPlanType,
  selectedOptionHasTrialBadge,
  hasTrialAvailable,
  isIOSApp,
}: ResolveCtaInput): ResolveCtaResult => {
  const shouldAllowRepurchaseCurrentPlan =
    selectedPlanType !== "lifetime" &&
    isSelectedCurrentPlan({ userPlanInfo, selectedPlanType }) &&
    userPlanInfo?.available_products?.find(
      (item) => item.product_id === userPlanInfo?.product?.id,
    );
  if (
    isSelectedCurrentPlan({ userPlanInfo, selectedPlanType }) &&
    !shouldAllowRepurchaseCurrentPlan
  ) {
    return {
      mode: "current",
      text: I18n.t("Current plan", { locale }),
      showCrypto: false,
    };
  }

  if (shouldAllowRepurchaseCurrentPlan) {
    return {
      mode: "action",
      text: I18n.t("Subscribe", { locale }),
      showCrypto: false,
    };
  }
  let text = "";
  if (
    selectedPlanType === "yearly" &&
    hasTrialAvailable &&
    selectedOptionHasTrialBadge
  ) {
    text = isIOSApp
      ? I18n.t("Start Your 7-Day Free Trial", { locale })
      : I18n.t("Start Your 7-Day Trial for $1.99", { locale });
  } else if (selectedPlanType === "lifetime") {
    text = I18n.t("Get Complete Forever", { locale });
  }
  // 如果当前套餐状态为隐藏,说明该套餐已下线, 不能进行升级，这里不处理
  if (userPlanInfo?.product?.status === "HIDDEN") {
    text = "";
  }

  return {
    mode: "action",
    text,
    showCrypto: false,
  };
};
